<template>
  <div>
    <v-row no-gutters align="center" class="mb-3" style="gap: 12px">
      <v-col cols="6" sm="4" md="3">
        <dropdown
          v-model="filterPeriod"
          :items="periodOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_date')"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <dropdown
          v-model="filterType"
          :items="typeOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_type_label')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-btn text small @click="resetFilters">{{ $t("finance_reset_filters") }}</v-btn>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="text-subtitle-1">{{ $t("finance_ledger_title") }}</v-card-title>
      <v-data-table
        :headers="headers"
        :items="transactions"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_transactions_filtered')"
        item-key="id"
        class="striped-table"
      >
        <template v-slot:item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template v-slot:item.type="{ item }">{{ describeType(item.entryType) }}</template>
        <template v-slot:item.debit="{ item }">
          <span style="color: #d9820f; font-weight: 600">
            {{ item.direction === "DEBIT" ? formatCurrency(item.amount) : "—" }}
          </span>
        </template>
        <template v-slot:item.credit="{ item }">
          <span style="color: #00b37e; font-weight: 600">
            {{ item.direction === "CREDIT" ? formatCurrency(item.amount) : "—" }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";

// Isti kodovi kao Knjiga salda (description) - vidi ticket #223672 v2.
const KNOWN_TYPES = [
  "ORDER_REVENUE",
  "COMMISSION",
  "CASH_RECEIVED",
  "PAYOUT",
  "SUBSCRIPTION",
  "REFUND",
];

const TYPE_LABEL_KEYS = {
  ORDER_REVENUE: "finance_desc_order_revenue",
  COMMISSION: "finance_desc_commission",
  CASH_RECEIVED: "finance_desc_cash_received",
  PAYOUT: "finance_desc_payout",
  SUBSCRIPTION: "finance_desc_subscription",
  REFUND: "finance_desc_refund",
};

const mapTransactionDto = (dto) => ({
  id: dto.id,
  entryType: dto.entry_type,
  direction: dto.direction,
  amount: Math.abs(Number(dto.amount) || 0),
  currency: dto.currency,
  sourceType: dto.source_type,
  sourceId: dto.source_id,
  note: dto.note,
  date: dto.created_at,
});

export default {
  name: "FinanceTabTransactions",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    transactions: [],
    filterPeriod: "30d",
    filterType: null,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { value: "7d", label: this.$t("finance_last_7_days") },
        { value: "30d", label: this.$t("finance_last_30_days") },
        { value: "90d", label: this.$t("finance_last_90_days") },
        { value: "all", label: this.$t("finance_all") },
      ];
    },

    typeOptions() {
      return KNOWN_TYPES.map((value) => ({ value, label: this.$t(TYPE_LABEL_KEYS[value]) }));
    },

    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_type_label"), value: "type" },
        { text: this.$t("finance_description"), value: "note" },
        { text: this.$t("finance_debit"), value: "debit", align: "end" },
        { text: this.$t("finance_credit"), value: "credit", align: "end" },
      ];
    },
  },

  watch: {
    filterPeriod() {
      this.load();
    },
    filterType() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    describeType(type) {
      const key = TYPE_LABEL_KEYS[type];
      return key ? this.$t(key) : (type || "").toLowerCase().replace(/_/g, " ");
    },

    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    resetFilters() {
      this.filterPeriod = "30d";
      this.filterType = null;
    },

    periodToFromDate() {
      if (this.filterPeriod === "all") return undefined;
      const days = { "7d": 7, "30d": 30, "90d": 90 }[this.filterPeriod];
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      return from.toISOString().slice(0, 10);
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getTransactions(this.restaurantId, {
          type: this.filterType || undefined,
          from: this.periodToFromDate(),
        });
        this.transactions = (data?.data || []).map(mapTransactionDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_transactions_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
